import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useUser } from '../../contexts/UserContext'
import { userService } from '../../services/userService'
import type { Contact, User, UserType } from '../../types/user'
import { getUserTypeDisplayName } from '../../utils/profileErrors'
import StatusChip from '../StatusChip'
import SearchBar from '../dashboard/SearchBar'
import Table from '../dashboard/Table'
import { AddContactModal } from './AddContactModal'
import { ExternalInviteModal } from './ExternalInviteModal'
import { TeamInviteModal } from './TeamInviteModal'

// Unified interface for displaying both contacts and team members
interface ContactOrMember {
  id: number
  name: string
  email: string
  type: 'contact' | 'team_member'
  user_type?: string
  stakeholder_types: string[]
  status: 'external' | 'team_member' | 'current_user'
  phone_number?: string
  original: Contact | User // Keep reference to original data
}

// Transform functions moved outside component to avoid useCallback dependencies
const transformContactToUnified = (contact: Contact): ContactOrMember => ({
  id: contact.id,
  name: contact.contact_object.name || contact.contact_object.email,
  email: contact.contact_object.email,
  type: 'contact',
  user_type: contact.contact_object.user_type,
  stakeholder_types: contact.contact_object.stakeholder_types || [],
  status: 'external', // All contacts (non-team members) are external
  phone_number: contact.contact_object.phone_number,
  original: contact,
})

const transformMemberToUnified = (
  member: User,
  currentUserId?: number,
): ContactOrMember => ({
  id: member.id,
  name:
    `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
    member.email,
  email: member.email,
  type: 'team_member',
  user_type: member.user_type,
  stakeholder_types: member.stakeholder_types || [],
  status: member.id === currentUserId ? 'current_user' : 'team_member',
  phone_number: member.phone_number,
  original: member,
})

interface AddContactsTableProps {
  onChange?: () => void
}

export const AddContactsTable: React.FC<AddContactsTableProps> = ({
  onChange,
}) => {
  const { user: currentUser } = useUser()
  const [contactsAndMembers, setContactsAndMembers] = useState<
    ContactOrMember[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showTeamInviteModal, setShowTeamInviteModal] = useState(false)
  const [showExternalInviteModal, setShowExternalInviteModal] = useState(false)
  const [showInviteDropdown, setShowInviteDropdown] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [filter, setFilter] = useState<'all' | 'contacts' | 'team_members'>(
    'all',
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [stakeholderFilter, setStakeholderFilter] = useState<string>('all')
  const inviteDropdownRef = useRef<HTMLDivElement>(null)
  const inviteButtonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const loadContactsAndMembers = useCallback(async () => {
    try {
      const [contactsData, membersData] = await Promise.all([
        userService.getContacts(),
        userService.getTeamMembers(),
      ])

      const unifiedContacts = contactsData.map(transformContactToUnified)
      const unifiedMembers = membersData.map((member) =>
        transformMemberToUnified(member, currentUser?.id),
      )

      setContactsAndMembers([...unifiedContacts, ...unifiedMembers])
      setError(null)
      if (onChange) onChange()
    } catch (err) {
      setError('Failed to load contacts and team members')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [onChange, currentUser?.id])

  useEffect(() => {
    loadContactsAndMembers()
  }, [loadContactsAndMembers])

  // Close dropdown when clicking outside and reposition on resize/scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inviteDropdownRef.current &&
        !inviteDropdownRef.current.contains(event.target as Node)
      ) {
        setShowInviteDropdown(false)
      }
    }

    const handleWindowChange = () => {
      // Recalculate dropdown position on viewport changes
      const btn = inviteButtonRef.current
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const dropdownWidth = 240
      const padding = 8
      let left = rect.left
      if (left + dropdownWidth + padding > viewportWidth) {
        left = Math.max(padding, viewportWidth - dropdownWidth - padding)
      } else {
        left = Math.max(padding, left)
      }
      const top = rect.bottom + padding
      setDropdownPosition({ top, left })
    }

    if (showInviteDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('resize', handleWindowChange)
      window.addEventListener('scroll', handleWindowChange, true)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', handleWindowChange)
      window.removeEventListener('scroll', handleWindowChange, true)
    }
  }, [showInviteDropdown])

  const handleInviteButtonClick = () => {
    if (!showInviteDropdown) {
      const btn = inviteButtonRef.current
      if (btn) {
        const rect = btn.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const dropdownWidth = 240
        const padding = 8
        let left = rect.left
        if (left + dropdownWidth + padding > viewportWidth) {
          left = Math.max(padding, viewportWidth - dropdownWidth - padding)
        } else {
          left = Math.max(padding, left)
        }
        const top = rect.bottom + padding
        setDropdownPosition({ top, left })
      }
    }
    setShowInviteDropdown(!showInviteDropdown)
  }

  const handleAddContact = () => {
    setShowAddModal(true)
  }

  const handleInviteTeamMember = () => {
    setShowInviteDropdown(false)
    setShowTeamInviteModal(true)
  }

  const handleInviteExternalMember = () => {
    setShowInviteDropdown(false)
    setShowExternalInviteModal(true)
  }

  const columnHelper = createColumnHelper<ContactOrMember>()

  const handleDelete = async (item: ContactOrMember) => {
    if (item.type === 'team_member') {
      alert('Cannot delete team members from this interface')
      return
    }

    if (!window.confirm('Are you sure you want to delete this contact?')) return

    try {
      setIsDeleting(true)
      await userService.deleteContact(item.id)
      await loadContactsAndMembers()
    } catch (err) {
      setError('Failed to delete contact')
      console.error(err)
    } finally {
      setIsDeleting(false)
    }
  }

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredData = contactsAndMembers.filter((item) => {
    if (filter === 'contacts' && item.type !== 'contact') return false
    if (filter === 'team_members' && item.type !== 'team_member') return false
    if (
      stakeholderFilter !== 'all' &&
      !item.stakeholder_types.includes(stakeholderFilter)
    )
      return false
    if (normalizedSearch) {
      const positionName = item.user_type
        ? getUserTypeDisplayName(item.user_type as UserType)
        : ''
      const haystack = [
        item.name,
        item.email,
        item.phone_number || '',
        positionName,
        item.stakeholder_types.join(' '),
      ]
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(normalizedSearch)) return false
    }
    return true
  })

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: (info) => {
        const status = info.getValue()
        return (
          <StatusChip variant={status}>
            {status === 'external' && 'External'}
            {status === 'team_member' && 'Team Member'}
            {status === 'current_user' && 'Me'}
          </StatusChip>
        )
      },
    }),
    columnHelper.accessor('phone_number', {
      id: 'phoneNumber',
      header: 'Phone Number',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('user_type', {
      id: 'userType',
      header: 'Position',
      cell: (info) => {
        const userType = info.getValue()
        return userType ? getUserTypeDisplayName(userType as UserType) : '-'
      },
    }),
    columnHelper.accessor('stakeholder_types', {
      id: 'stakeholderTypes',
      header: 'Stakeholder Type',
      cell: (info) => info.getValue()?.join(', ') || '-',
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: 'Email',
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => {
        const item = info.row.original
        return item.type === 'contact' ? (
          <button
            className={css(styles.deleteButton)}
            onClick={() => handleDelete(item)}
            disabled={isDeleting}
          >
            <i className="fa-solid fa-minus" />
          </button>
        ) : (
          <span className={css(styles.teamMemberIndicator)}>
            <i className="fa-solid fa-users" />
          </span>
        )
      },
    }),
  ]

  if (loading)
    return (
      <div className={css(styles.loadingContainer)}>
        <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-3x" />
        <p className={css(styles.loadingText)}>Loading contacts...</p>
      </div>
    )
  if (error) return <div>Error: {error}</div>

  return (
    <div className={css(styles.root)}>
      <div className={css(styles.header)}>
        <div className={css(styles.titleSection)}>
          <h2 className={css(styles.title)}>Directory</h2>
          <div className={css(styles.filterButtons)}>
            <button
              className={css(
                styles.filterButton,
                filter === 'all' && styles.activeFilter,
              )}
              onClick={() => setFilter('all')}
            >
              All ({contactsAndMembers.length})
            </button>
            <button
              className={css(
                styles.filterButton,
                filter === 'contacts' && styles.activeFilter,
              )}
              onClick={() => setFilter('contacts')}
            >
              Contacts (
              {
                contactsAndMembers.filter((item) => item.type === 'contact')
                  .length
              }
              )
            </button>
            <button
              className={css(
                styles.filterButton,
                filter === 'team_members' && styles.activeFilter,
              )}
              onClick={() => setFilter('team_members')}
            >
              Team (
              {
                contactsAndMembers.filter((item) => item.type === 'team_member')
                  .length
              }
              )
            </button>
            <select
              className={css(styles.stakeholderSelect)}
              value={stakeholderFilter}
              onChange={(e) => setStakeholderFilter(e.target.value)}
              aria-label="Filter by stakeholder type"
            >
              <option value="all">All Stakeholders</option>
              <option value="CONTRACTOR">Contractor</option>
              <option value="BROKER">Broker</option>
              <option value="SUB_HAULER">Sub Hauler</option>
            </select>
          </div>
        </div>
        <div className={css(styles.searchSection)}>
          <SearchBar
            onSearch={setSearchTerm}
            placeholder="Search by name, email, phone, or role"
          />
        </div>
        <div className={css(styles.buttonContainer)}>
          <div
            className={css(styles.inviteButtonContainer)}
            ref={inviteDropdownRef}
          >
            <button
              className={css(styles.button)}
              onClick={handleInviteButtonClick}
              ref={inviteButtonRef}
            >
              <i className="fa-solid fa-plus" /> Invite
              <i
                className={`fa-solid fa-chevron-down ${css(styles.chevronIcon, showInviteDropdown && styles.chevronIconRotated)}`}
              />
            </button>
            {showInviteDropdown && (
              <div
                className={css(styles.dropdown)}
                style={{
                  position: 'fixed',
                  top: dropdownPosition?.top,
                  left: dropdownPosition?.left,
                }}
              >
                <button
                  className={css(styles.dropdownItem)}
                  onClick={handleInviteTeamMember}
                >
                  <i className="fa-solid fa-users" />
                  Invite Team Member
                </button>
                <button
                  className={css(styles.dropdownItem)}
                  onClick={handleInviteExternalMember}
                >
                  <i className="fa-solid fa-user-plus" />
                  Invite External Member
                </button>
              </div>
            )}
          </div>
          <button className={css(styles.button)} onClick={handleAddContact}>
            <i className="fa-solid fa-plus" /> Contact
          </button>
        </div>
      </div>
      <div className={css(styles.tableContainer)}>
        <div className={css(styles.tableWrapper)}>
          <Table data={filteredData} columns={columns} enableSorting />
        </div>
      </div>
      {showAddModal && (
        <AddContactModal
          onClose={() => setShowAddModal(false)}
          onSuccess={loadContactsAndMembers}
        />
      )}
      {showTeamInviteModal && (
        <TeamInviteModal
          onClose={() => setShowTeamInviteModal(false)}
          onSuccess={loadContactsAndMembers}
        />
      )}
      {showExternalInviteModal && (
        <ExternalInviteModal
          onClose={() => setShowExternalInviteModal(false)}
          onSuccess={loadContactsAndMembers}
        />
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px',
    flexShrink: 0,
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 12,
      padding: 12,
    },
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 8,
    },
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    '@media (max-width: 1024px)': {
      fontSize: '20px',
    },
  },
  filterButtons: {
    display: 'flex',
    gap: '8px',
    marginLeft: '10px',
    '@media (max-width: 1024px)': {
      marginLeft: 0,
      flexWrap: 'wrap',
    },
  },
  filterButton: {
    backgroundColor: '#E5E7EB',
    color: '#4B5563',
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  activeFilter: {
    backgroundColor: '#1C64F2',
    color: 'white',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  stakeholderSelect: {
    backgroundColor: '#E5E7EB',
    color: '#4B5563',
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  searchSection: {
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 1024px)': {
      width: '100%',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: '8px',
    '@media (max-width: 1024px)': {
      marginTop: 8,
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
    },
  },
  inviteButtonContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  button: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  chevronIcon: {
    fontSize: '12px',
    marginLeft: '4px',
    transition: 'transform 0.2s ease',
  },
  chevronIconRotated: {
    transform: 'rotate(180deg)',
  },
  dropdown: {
    // Positioned via inline fixed coordinates to avoid clipping and viewport overflow
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 10,
    minWidth: '200px',
    marginTop: '4px',
    maxWidth: 'calc(100vw - 16px)',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '14px',
    color: '#1F2937',
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
    ':first-child': {
      borderTopLeftRadius: '6px',
      borderTopRightRadius: '6px',
    },
    ':last-child': {
      borderBottomLeftRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  tableContainer: {
    borderRadius: '8px',
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  tableWrapper: {
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'auto',
    height: '100%',
    '> div': {
      height: '100%',
    },
  },
  deleteButton: {
    padding: '4px',
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#DC2626',
    },
  },
  teamMemberIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: '#6B7280',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
})

export default AddContactsTable
